import { PropsWithChildren } from 'react'

import {
  Box,
  Card,
  type Colors,
  Spinner,
  useBreakpoint,
} from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import BarChart from 'modules/core/components/BarChart'
import i18n from 'modules/core/lib/i18n'

import performanceWeightsUpsell from 'modules/performance_process/lib/assets/performanceWeightsUpsell.png'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { ChartSkeleton } from '../ChartSkeleton'
import { GROUP_BY_COLORS, GroupByType } from '../helpers'
import * as styles from './index.vanilla.css'

type Margin = {
  top: number
  bottom: number
  left: number
  right: number
}

const DEFAULT_MARGIN: Margin = {
  top: 35,
  bottom: 90,
  left: 30,
  right: -20,
}

export interface ChartData {
  bucket: string
  score?: number
  color: Colors
}

export type ChartDataWithDynamicKeys = ChartData & Record<string, string | number>

interface ReviewEmployeeScoreChartProps {
  hasRecords?: boolean
  data: ChartDataWithDynamicKeys[]
  total: number | null | undefined
  loading?: boolean
  tooltip?: string
  groupBy?: GroupByType
  showGroupBy?: boolean
  groupKeys?: string[]
  margin?: Margin
  onBarClick?: (datum: { index: number; label: string; value: number | null }) => void
  showEnterpriseUpsellingKit?: boolean
}

export const ReviewEmployeeScoreChart = ({
  data,
  total,
  loading,
  tooltip,
  children,
  onBarClick,
  groupKeys,
  margin = DEFAULT_MARGIN,
  showGroupBy = false,
  hasRecords = true,
  showEnterpriseUpsellingKit = false,
}: PropsWithChildren<ReviewEmployeeScoreChartProps>) => {
  const { isMobile, breakpoint } = useBreakpoint()

  if (loading && data.length === 0) {
    return (
      <Box height="s400" width="full">
        <Card>
          <Card.Content>
            <Box alignSelf="center" flexDirection="column">
              <Box paddingTop="s140">
                <Spinner />
              </Box>
            </Box>
          </Card.Content>
        </Card>
      </Box>
    )
  }

  const isEmpty = total === 0 || data.length === 0
  const keys = showGroupBy ? groupKeys ?? [] : ['score']
  const colors = showGroupBy ? GROUP_BY_COLORS : data.map(({ color }) => color)

  return (
    <Box height="s400" width="full" style={{ position: 'relative' }}>
      <Card>
        <Card.Title size="small" tooltip={tooltip}>
          {i18n.t('performance_process.analytics.score_distribution_chart.title')}
        </Card.Title>

        <Card.Content>
          {children}
          {isEmpty || !hasRecords ? (
            <ChartSkeleton hasRecords={hasRecords} />
          ) : (
            <>
              {showEnterpriseUpsellingKit && (
                <Box
                  flexDirection="row"
                  justifyContent="flexEnd"
                  width="full"
                  className={styles.upsellingPosition}
                >
                  <Box width="fit-content">
                    <UpsellingKit.ProductWidgetPopover
                      label={i18n.t('performance_process.upsells.weights.button')}
                      mediaUrl={performanceWeightsUpsell}
                      variant="outlinePromote"
                      title={i18n.t('performance_process.upsells.weights.title_review')}
                      description={i18n.t('performance_process.upsells.weights.description')}
                      requestSource="Performance - weights enterprise plan - score chart"
                      moduleRequested={AvailableModules.review_process}
                      featureRequested={Features.review_process_custom_score_configuration}
                      requestReason={ValidReasons.MissingFeature}
                      size="sm"
                    />
                  </Box>
                </Box>
              )}

              <BarChart
                margin={margin}
                colors={colors}
                colorBy={showGroupBy ? 'id' : 'indexValue'}
                maxValue={'auto'}
                keys={keys}
                indexBy={'bucket'}
                groupMode={showGroupBy ? 'stacked' : 'grouped'}
                axisBottom={{
                  format: (label: string) => label,
                  renderTick: ({ x, y, value }) => (
                    <g transform={`translate(${x},${y + 12})`}>
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontFamily: 'Inter',
                          fontSize: data.length > 5 || isMobile || breakpoint === 'l' ? 9 : 12,
                        }}
                      >
                        {value}
                      </text>
                    </g>
                  ),
                }}
                showLegend={showGroupBy}
                useCustomTooltip={false}
                enabledGridY
                data={data}
                isInteractive={!isEmpty}
                onClick={(datum) => !isEmpty && onBarClick?.(datum)}
                pointer
              />
            </>
          )}
        </Card.Content>
      </Card>
    </Box>
  )
}
